import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import ReactCountryFlag from 'react-country-flag';
import useNow from '../../hooks/useNow';
import { calculateVoteWeight, calculateTelosRewards } from '../../utils/voteCalculations';
import { turnAt, msUntilTurn, formatDuration, scheduleStatus } from '../../utils/schedule';
import ProducerLinks from '../Monitor/ProducerLinks'; // directo: evita import circular con el barrel de Monitor

const TOP_N = 21;

const compact = (v) => new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }).format(v);

export const STATUS_BADGE = {
  live: ['Producing now', 'bg-[#16a34a] text-white'],
  in_schedule: ['In schedule', 'bg-green-100 text-green-800'],
  extra_in_schedule: ['In schedule', 'bg-green-100 text-green-800'],
  rotated_in: ['Rotated in · standby', 'bg-teal-100 text-teal-800'],
  rotated_out: ['Rotated out', 'bg-amber-100 text-amber-800'],
  not_in_schedule: ['Not in schedule', 'bg-amber-100 text-amber-800'],
  standby: ['Standby', 'bg-gray-100 text-gray-600']
};

export const StatusBadge = ({ status }) => {
  const [label, cls] = STATUS_BADGE[status] || STATUS_BADGE.standby;
  return <span className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${cls}`}>{label}</span>;
};

/**
 * Tabla de productores con su posición en el schedule.
 * Orden "By votes" (ranking) o "By schedule" (orden de producción).
 *
 * Props: producers (ordenados por votos, de useMonitorData), active (schedule),
 *        chainFamily, currentPrice
 */
const ProducersScheduleTable = ({ producers, active, chainFamily, currentPrice, lagMs = 0 }) => {
  const now = useNow(1000) - lagMs;
  const [sort, setSort] = useState('votes');
  const isTelos = chainFamily === 'TELOS';
  const n = active.length;
  const cur = n ? turnAt(n, now).index : -1;

  const weights = producers.map((p) => calculateVoteWeight(parseFloat(p.total_votes), chainFamily));
  const v21 = weights[TOP_N - 1];
  const v22 = weights[TOP_N];

  let rows = producers.map((p, i) => {
    const rank = i + 1;
    const slot = active.indexOf(p.owner); // -1 = fuera del schedule
    const inSchedule = slot >= 0;
    const status = inSchedule && slot === cur ? 'live' : scheduleStatus({ rank, inSchedule, isTelos, topN: TOP_N });
    let margin = null;
    if (rank <= TOP_N && v22 != null) margin = { ahead: true, value: weights[i] - v22 };
    else if (rank > TOP_N && v21 != null) margin = { ahead: false, value: v21 - weights[i] };
    return { p, rank, slot, status, weight: weights[i], margin };
  });

  if (sort === 'schedule') {
    rows = [...rows].sort((a, b) => (a.slot < 0 ? 999 : a.slot) - (b.slot < 0 ? 999 : b.slot) || a.rank - b.rank);
  }

  const segBtn = (key, label) => (
    <button
      type="button"
      onClick={() => setSort(key)}
      aria-pressed={sort === key}
      className={`h-[30px] px-3.5 rounded-md text-[13px] font-medium ${
        sort === key ? 'bg-surface text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-900'
      }`}
    >
      {label}
    </button>
  );

  return (
    <section aria-label="Producers" className="card overflow-hidden">
      <div className="card-header">
        <div>
          <h2 className="card-title m-0">All active producers</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            {producers.length} active{n ? ` · ${n} in schedule` : ''} · votes updated every 2 minutes
          </p>
        </div>
        <div role="group" aria-label="Sort producers" className="flex p-0.5 rounded-lg border border-gray-200 bg-gray-50">
          {segBtn('votes', 'By votes')}
          {segBtn('schedule', 'By schedule')}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="text-left text-xs text-gray-500 border-b border-gray-100">
              <th className="py-2.5 pl-5 pr-2 font-medium">Rank</th>
              <th className="py-2.5 px-2 font-medium">Producer</th>
              <th className="py-2.5 px-2 font-medium hidden lg:table-cell">Country</th>
              <th className="py-2.5 px-2 font-medium text-right">Votes</th>
              <th className="py-2.5 px-2 font-medium text-right" title="Votes ahead of #22 (top 21) or needed to reach #21 (standbys)">Margin</th>
              <th className="py-2.5 px-2 font-medium">Slot</th>
              <th className="py-2.5 px-2 font-medium">Produces</th>
              <th className="py-2.5 px-2 font-medium">Status</th>
              {isTelos && <th className="py-2.5 px-2 font-medium text-right whitespace-nowrap">Rewards 24h</th>}
              <th className="py-2.5 pl-2 pr-5 font-medium hidden xl:table-cell">Links</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ p, rank, slot, status, weight, margin }) => {
              const info = p.producerInfo;
              const website = info?.website || info?.url;
              const boosted = isTelos && p.selfVoteBoosted > 0 ? p.selfVoteBoosted / 10000 : 0;
              return (
                <tr key={p.owner} className={`border-t border-gray-100 ${status === 'live' ? 'bg-green-50' : rank === TOP_N + 1 && sort === 'votes' ? 'border-t-2 border-t-gray-300' : ''}`}>
                  <td className="py-2.5 pl-5 pr-2 text-gray-500">{rank}</td>
                  <td className="py-2.5 px-2">
                    <span className="flex items-center gap-2 min-w-0">
                      <span className="w-7 h-7 shrink-0">
                        {info?.logo && (
                          <img src={info.logo} alt="" className="w-7 h-7 object-contain" loading="lazy" onError={(e) => { e.target.style.display = 'none'; }} />
                        )}
                      </span>
                      <Link to={`/account-detail/${p.owner}`} className="link truncate">{p.owner}</Link>
                    </span>
                  </td>
                  <td className="py-2.5 px-2 text-gray-700 hidden lg:table-cell whitespace-nowrap">
                    {info?.country ? (
                      <span className="inline-flex items-center gap-1.5">
                        <ReactCountryFlag countryCode={info.country} svg style={{ width: '1em', height: '1em' }} />
                        {info.country}
                      </span>
                    ) : <span className="text-gray-400">—</span>}
                  </td>
                  <td className="py-2.5 px-2 text-right whitespace-nowrap">
                    <span className="font-mono text-[13px]">{weight.toLocaleString('en-US', { maximumFractionDigits: 0 })}</span>
                    {boosted > 0 && (
                      <span className="block text-[11px] text-green-700">+{boosted < 1 ? boosted.toFixed(2) : compact(boosted)} boosted</span>
                    )}
                  </td>
                  <td className={`py-2.5 px-2 text-right font-mono text-xs whitespace-nowrap ${margin?.ahead ? 'text-gray-700' : 'text-gray-500'}`}>
                    {margin ? `${margin.ahead ? '+' : '−'}${compact(Math.max(0, margin.value))}` : '—'}
                    {margin && !margin.ahead && <span className="text-[10px] font-sans"> to #21</span>}
                  </td>
                  <td className={`py-2.5 px-2 font-mono text-[13px] ${slot >= 0 ? 'text-gray-900' : 'text-gray-400'}`}>
                    {slot >= 0 ? `#${slot + 1}` : '—'}
                  </td>
                  <td className="py-2.5 px-2 text-xs text-gray-700 whitespace-nowrap">
                    {slot < 0 ? '' : status === 'live' ? 'now' : `in ${formatDuration(msUntilTurn(slot, n, now))}`}
                  </td>
                  <td className="py-2.5 px-2"><StatusBadge status={status} /></td>
                  {isTelos && (
                    <td className="py-2.5 px-2 text-right whitespace-nowrap text-gray-700">
                      {calculateTelosRewards(rank, currentPrice?.price || 0, producers.length).toFixed(2)} <span className="text-gray-500">TLOS</span>
                    </td>
                  )}
                  <td className="py-2.5 pl-2 pr-5 hidden xl:table-cell">
                    <ProducerLinks website={website} twitter={info?.twitter} telegram={info?.telegram} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
};

export default ProducersScheduleTable;
